import {
  LOT_PROSPECTS_B2B,
  CATALOGUE_STANDARD,
  CATALOGUE_WORDPRESS,
  LOTS_EN,
  LOTS_OBLIGATOIRES,
  type CatalogueLot,
  type VitrineLanguage,
  type VitrineCatalogueKind,
} from "@/lib/vitrine/catalogue";
import type {
  VitrineArborescence,
  VitrineExtraction,
  VitrinePricing,
  VitrinePricingLine,
} from "@/lib/vitrine/types";

// Chiffrage vitrine. Variante standard : 100 % déterministe (mêmes règles =>
// mêmes lignes d'une page à l'autre pour un même brief type, OFFRE_VITRINE.md
// "heures identiques d'une page à l'autre"). Variante WordPress : les lots sont
// choisis par l'IA (le brief y est une liste de tâches hétérogènes), mais les
// heures et les montants viennent toujours du catalogue, et `assembleLines`
// applique les mêmes garde-fous.

const LEGAL_RE = /mentions|l[ée]gal|cgv|cgu|confidentialit|cookies|privacy|terms/i;
const ACCUEIL_RE = /^accueil|^home|page d'accueil/i;

function lot(catalogue: CatalogueLot[], id: string): CatalogueLot {
  const found = catalogue.find((l) => l.id === id);
  if (!found) throw new Error(`Lot inconnu : ${id}`);
  return found;
}

function toLine(
  l: CatalogueLot,
  quantite: number,
  kind: "base" | "option",
  lang: VitrineLanguage = "fr"
): VitrinePricingLine {
  const en = lang === "en" ? LOTS_EN[l.id] : undefined;
  return {
    lotId: l.id,
    label: en?.label ?? l.label,
    description: en?.description ?? l.description,
    quantite: l.unite ? Math.max(1, Math.min(30, Math.round(quantite))) : 1,
    minutesUnitaires: l.minutes,
    montantUnitaire: l.montant,
    kind,
  };
}

function mentions(extraction: VitrineExtraction, re: RegExp): boolean {
  return [...extraction.pagesDemandees, ...extraction.fonctionnalitesDemandees].some((s) =>
    re.test(s)
  );
}

/** Nombre de pages de contenu hors accueil et pages légales. */
export function countContentPages(
  extraction: VitrineExtraction,
  arborescence: VitrineArborescence | null
): number {
  const names = arborescence?.pages.map((p) => p.nom) ?? extraction.pagesDemandees;
  const n = names.filter((n) => !LEGAL_RE.test(n) && !ACCUEIL_RE.test(n.trim())).length;
  // Brief sans liste de pages : base prudente de 4 pages (présentation,
  // services, réalisations ou équivalent, contact).
  return n > 0 ? n : 4;
}

// Pages de contenu chiffrées UNE PAR UNE d'après l'arborescence : temps et
// prix suivent le nombre de blocs de la page (1 à 5), pour un devis qui colle
// au site réellement demandé (demande de Thibault, 2026-09-27 : précision
// indispensable sur les petits budgets). Montant non proportionnel au temps
// (pas de taux horaire déductible). 3 blocs = 1h01 / 70 €, comme le lot
// générique "page-contenu" utilisé quand l'arborescence manque.
const PAGE_MINUTES_BASE = 25;
const PAGE_MINUTES_PAR_BLOC = 12;
const PAGE_MONTANT_BASE = 40;
const PAGE_MONTANT_PAR_BLOC = 10;

function contentPageLines(
  arborescence: VitrineArborescence | null,
  lang: VitrineLanguage = "fr"
): VitrinePricingLine[] | null {
  const pages = (arborescence?.pages ?? []).filter(
    (p) => !LEGAL_RE.test(p.nom) && !ACCUEIL_RE.test(p.nom.trim())
  );
  if (pages.length === 0) return null;
  return pages.map((p, i) => {
    const blocs = Math.max(1, Math.min(5, p.sections.length || 3));
    return {
      lotId: `page-contenu:${i}`,
      label: lang === "en" ? `"${p.nom}" page` : `Page « ${p.nom} »`,
      description: p.sections.length
        ? p.sections.join(", ")
        : lang === "en"
          ? "Page layout."
          : "Mise en forme de la page.",
      quantite: 1,
      minutesUnitaires: PAGE_MINUTES_BASE + PAGE_MINUTES_PAR_BLOC * blocs,
      montantUnitaire: PAGE_MONTANT_BASE + PAGE_MONTANT_PAR_BLOC * blocs,
      kind: "base",
    };
  });
}

/**
 * Budget serré (tranche "moins de 500 €") : la base ne contient que ce que le
 * brief demande et le strict nécessaire à un site en ligne ; tout le reste
 * passe en options cochables. Le client voit exactement ce que paie son
 * budget, et peut ajouter le reste lui-même.
 */
export function buildStandardPricing(
  extraction: VitrineExtraction,
  arborescence: VitrineArborescence | null,
  opts: { budgetSerre?: boolean; language?: VitrineLanguage } = {}
): VitrinePricing {
  const c = CATALOGUE_STANDARD;
  const serre = opts.budgetSerre === true;
  const lang = opts.language ?? "fr";
  const lines: VitrinePricingLine[] = [];
  const add = (id: string, q = 1, kind: "base" | "option" = "base") =>
    lines.push(toLine(lot(c, id), q, kind, lang));
  // Hors budget serré, un signal suffit à inclure le lot ; en budget serré,
  // seule une demande explicite l'inclut, sinon il devient une option.
  const inclure = (demande: boolean, signal: boolean): "base" | "option" | null =>
    demande || (signal && !serre) ? "base" : signal ? "option" : null;

  add("cadrage");
  add("maquette-accueil");
  add("integration-accueil");
  const pageLines = contentPageLines(arborescence, lang);
  if (pageLines) lines.push(...pageLines);
  else add("page-contenu", countContentPages(extraction, arborescence));
  add("back-office");
  add(extraction.signauxDemo.formulaireMetier ? "formulaire-metier" : "formulaire-contact");

  const optionnels: [string, "base" | "option" | null][] = [
    ["galerie", inclure(mentions(extraction, /galerie|r[ée]alisation|portfolio|photos|gallery|projects/i), extraction.signauxDemo.photosRealisations)],
    ["carte", inclure(mentions(extraction, /carte|annuaire|agences|[ée]tablissements|\bmap\b|directory|locations|branches/i), extraction.signauxDemo.multiSites)],
    ["estimateur", inclure(mentions(extraction, /estimat|simulat|calculat|devis en ligne|online quote/i), extraction.signauxDemo.estimation)],
    // Le blog n'est jamais inclus sans demande : option par défaut, quel que soit le budget.
    ["blog", mentions(extraction, /blog|actualit|article|news/i) ? "base" : "option"],
  ];
  for (const [id, kind] of optionnels) if (kind === "base") add(id);

  add("seo-base");
  const aLocalite = !!(extraction.localite.ville || extraction.localite.departement);
  const localDemande = extraction.criteres.some((s) => /local|google business|gbp|fiche google/i.test(s));
  const seoLocal = inclure(localDemande, aLocalite);
  if (seoLocal === "base") add("seo-local");
  const iaDemandee = extraction.criteres.some((s) => /\bia\b|\bai\b|chatgpt|intelligence artificielle|artificial intelligence|assistant/i.test(s));
  const ia = serre && !iaDemandee ? "option" : "base";
  if (ia === "base") add("visibilite-ia");
  if (extraction.projet === "refonte") {
    add("reprise-contenu", (pageLines?.length ?? countContentPages(extraction, arborescence)) + 1);
    add("redirections");
  }
  add("legal");
  add("mise-en-ligne");
  add("prise-en-main");

  // Options cochables, après la base.
  for (const [id, kind] of optionnels) if (kind === "option") add(id, 1, "option");
  if (seoLocal !== "base") add("seo-local", 1, "option");
  if (ia === "option") add("visibilite-ia", 1, "option");

  return { catalogue: "standard", lines };
}

/**
 * Variante WordPress : assemble les lots choisis par l'IA en appliquant les
 * garde-fous (ids connus uniquement, doublons fusionnés, quantités bornées,
 * lots obligatoires ajoutés s'ils manquent).
 */
export function assembleWordpressPricing(
  choix: { lotId: string; quantite: number; kind: "base" | "option" }[],
  generatedPrompt: string | null,
  lang: VitrineLanguage = "fr"
): VitrinePricing {
  const c = CATALOGUE_WORDPRESS;
  const byId = new Map<string, VitrinePricingLine>();
  for (const ch of choix) {
    const l = c.find((x) => x.id === ch.lotId);
    if (!l) continue;
    const existing = byId.get(l.id);
    if (existing) {
      if (l.unite) existing.quantite = Math.min(30, existing.quantite + Math.max(1, ch.quantite));
      if (ch.kind === "base") existing.kind = "base";
      continue;
    }
    byId.set(l.id, toLine(l, ch.quantite, ch.kind, lang));
  }
  for (const id of LOTS_OBLIGATOIRES.wordpress) {
    if (!byId.has(id)) byId.set(id, toLine(lot(c, id), 1, "base", lang));
    else byId.get(id)!.kind = "base";
  }
  // Ordre du catalogue (lecture chronologique : audit -> mise en production).
  const lines = c.map((l) => byId.get(l.id)).filter((l): l is VitrinePricingLine => !!l);
  return { catalogue: "wordpress", lines, generatedPrompt };
}

export function lineTotal(l: VitrinePricingLine): number {
  return l.montantUnitaire * l.quantite;
}

export function pricingTotals(
  pricing: VitrinePricing,
  optionsActives: Set<string> = new Set()
): { montant: number; minutes: number } {
  let montant = 0;
  let minutes = 0;
  for (const l of pricing.lines) {
    if (l.kind === "base" || optionsActives.has(l.lotId)) {
      montant += lineTotal(l);
      minutes += l.minutesUnitaires * l.quantite;
    }
  }
  return { montant, minutes };
}

export type { VitrineCatalogueKind };

// Mots qui signalent une clientèle professionnelle, pour les extractions
// antérieures au champ `clienteleB2B` (ou quand le modèle l'a omis).
const B2B_RE =
  /syndic|bailleur|entreprise|professionnel|b2b|fonci[eè]re|administrateur|collectivit|pme|commer[cç]ant|gestionnaire|industriel|cabinet|agence|business|compan(y|ies)|corporate|\bfirms?\b|landlord|property manag/i;

export function isClienteleB2B(extraction: VitrineExtraction): boolean {
  return extraction.clienteleB2B === true || B2B_RE.test(extraction.clientele ?? "");
}

/**
 * Ajoute l'option "500 prospects qualifiés" quand la cible est B2B (variante
 * standard comme WordPress), avec la clientèle du brief dans la description.
 */
export function withProspectsOption(
  pricing: VitrinePricing,
  extraction: VitrineExtraction,
  lang: VitrineLanguage = "fr"
): VitrinePricing {
  if (!isClienteleB2B(extraction) || pricing.lines.some((l) => l.lotId === LOT_PROSPECTS_B2B.id)) {
    return pricing;
  }
  const line = toLine(LOT_PROSPECTS_B2B, 1, "option", lang);
  const cible = extraction.clientele?.trim();
  if (cible && lang === "en") {
    line.description = `500 decision-makers in your target market (${cible}): first name, last name, job title, verified business email, ready to contact. Sources stated, within the GDPR framework for business-to-business prospecting.`;
  } else if (cible) {
    line.description = `500 décideurs parmi votre cible (${cible}) : nom, prénom, poste, e-mail professionnel vérifié, prêts à contacter. Sources indiquées, dans le cadre du RGPD pour la prospection entre professionnels.`;
  }
  return { ...pricing, lines: [...pricing.lines, line] };
}
